import { ROLES } from "./constants";

const CAN_MANAGE_ONBOARDING = [
  ROLES.SYSTEM_OWNER, ROLES.SECURITY_ADMIN, ROLES.SYSTEM_ADMIN,
  ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER,
];

const CAN_MANAGE_USERS = [ROLES.SYSTEM_OWNER, ROLES.SECURITY_ADMIN, ROLES.SYSTEM_ADMIN];

// Mirrors backend EMPLOYEE_READ_ALL grantees (constants/permissions.py ROLE_PERMISSIONS) -
// everyone else only holds EMPLOYEE_READ_ASSIGNED (their own linked/assigned employees, not
// the full company-wide directory) and gets a 403 from GET /employees.
const CAN_VIEW_EMPLOYEES = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.SECURITY_ADMIN,
  ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER, ROLES.LABOR_COMPLIANCE_OFFICER, ROLES.READ_ONLY_AUDITOR,
];
export function canViewEmployees(role) {
  return CAN_VIEW_EMPLOYEES.includes(role);
}

// Mirrors backend EMPLOYEE_INVITATION_CREATE/RESEND/REVOKE grantees - the STAFF/employee
// invitation workflow only. Deliberately excludes SECURITY_ADMIN (auth/MFA/security only).
const CAN_CREATE_EMPLOYEE_INVITATION = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER,
];

// Mirrors backend LABOR_COMPLIANCE_READ grantees (constants/permissions.py).
const CAN_VIEW_LABOR_COMPLIANCE = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.LABOR_COMPLIANCE_OFFICER,
];
// Mirrors backend ARTICLE36_AGREEMENT_CREATE/UPDATE_DRAFT/REVIEW/APPROVE/ACTIVATE grantees -
// full Article 36 management is LABOR_COMPLIANCE_OFFICER + SYSTEM_OWNER/SYSTEM_ADMIN only.
const CAN_MANAGE_ARTICLE36 = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.LABOR_COMPLIANCE_OFFICER];

// Mirrors backend LABOR_COMPLIANCE_REVIEW grantees (constants/permissions.py) - update
// compliance source status + corrective action status.
const CAN_REVIEW_LABOR_COMPLIANCE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.LABOR_COMPLIANCE_OFFICER];

export function isManagementRole(role) {
  return role && role !== ROLES.EMPLOYEE;
}

export function canManageOnboarding(role) {
  return CAN_MANAGE_ONBOARDING.includes(role);
}

// Mirrors backend RECRUITMENT_JOB_MANAGE/RECRUITMENT_APPLICANT_REVIEW/RECRUITMENT_INTERVIEW_MANAGE
// grantees (constants/permissions.py _RECRUITMENT_FULL) - same recruiter-facing roles that
// already process Employee Applications and Migration.
export function canManageRecruitment(role) {
  return CAN_MANAGE_ONBOARDING.includes(role);
}

// Mirrors backend _RECRUITMENT_INTERVIEW_FULL grantees (constants/permissions.py) - the
// interview scheduling engine's calendar/availability pages. Deliberately excludes
// SECURITY_ADMIN and STORE_MANAGER (calendar access is never automatic - see
// GRANTABLE_EXTRA_PERMISSIONS for the STORE_MANAGER break-glass grant path).
const CAN_MANAGE_INTERVIEWS = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER];
export function canManageInterviews(role) {
  return CAN_MANAGE_INTERVIEWS.includes(role);
}

export function canManageDocumentReview(role) {
  return CAN_MANAGE_ONBOARDING.includes(role);
}

export function canManageUsers(role) {
  return CAN_MANAGE_USERS.includes(role);
}

export function canCreateEmployeeInvitation(role) {
  return CAN_CREATE_EMPLOYEE_INVITATION.includes(role);
}

// MANAGEMENT-domain account creation / management-role assignment is a per-user grant, not
// purely role-based (see backend constants/permissions.py GRANTABLE_EXTRA_PERMISSIONS) -
// SYSTEM_OWNER always holds it; anyone else (in practice only ever SYSTEM_ADMIN) only holds
// it via an explicit `extra_permissions` entry on their own account.
export function canCreateManagementUser(user) {
  if (!user) return false;
  if (user.role === ROLES.SYSTEM_OWNER) return true;
  return (user.extra_permissions || []).includes("management.user.create");
}

export function canAssignManagementRole(user) {
  if (!user) return false;
  if (user.role === ROLES.SYSTEM_OWNER) return true;
  return (user.extra_permissions || []).includes("management.role.assign");
}

// Only SYSTEM_OWNER may grant/revoke the break-glass permissions above to a SYSTEM_ADMIN.
export function canGrantExtraPermissions(role) {
  return role === ROLES.SYSTEM_OWNER;
}

const CAN_ARCHIVE_EMPLOYEE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.SECURITY_ADMIN];
export function canArchiveEmployee(role) {
  return CAN_ARCHIVE_EMPLOYEE.includes(role);
}
export function canReactivateEmployee(role) {
  return CAN_ARCHIVE_EMPLOYEE.includes(role);
}

// Test/QA/demo/E2E fixture cleanup (see backend employee_cleanup_service.py). PREVIEW
// (read-only classification + dependency map) is available to any admin-tier role; EXECUTE
// (actually deleting) and BULK (operating on more than one record at once) are per-user
// break-glass grants just like management.user.create above - SYSTEM_OWNER always holds
// them, SYSTEM_ADMIN only via an explicit extra_permissions entry.
export function canPreviewTestCleanup(role) {
  return CAN_ARCHIVE_EMPLOYEE.includes(role);
}
export function canExecuteTestCleanup(user) {
  if (!user) return false;
  if (user.role === ROLES.SYSTEM_OWNER) return true;
  return (user.extra_permissions || []).includes("employee.test_cleanup.execute");
}
export function canBulkTestCleanup(user) {
  if (!user) return false;
  if (user.role === ROLES.SYSTEM_OWNER) return true;
  return (user.extra_permissions || []).includes("employee.test_cleanup.bulk");
}

export function canReadAudit(role) {
  return isManagementRole(role);
}

// Store-scope architecture (Phase 2 P0) - deliberately narrower than canManageUsers (which
// also includes SECURITY_ADMIN): assigning store/organization scope is an operational
// authority matching backend USER_SCOPE_MANAGE, held only by SYSTEM_OWNER/SYSTEM_ADMIN.
export function canManageStoreAccess(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}
export function canGrantOrgWideScope(user) {
  if (!user) return false;
  if (user.role === ROLES.SYSTEM_OWNER) return true;
  return (user.extra_permissions || []).includes("grant_org_wide_scope");
}
export function canReviewStoreAssignments(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}
export function canTransferEmployeeStore(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}

// Operational Hardening (2026-08-10) - mirrors backend SYSTEM_JOBS_READ/MANAGE and
// PRODUCTION_READINESS_READ, held only by SYSTEM_OWNER/SYSTEM_ADMIN (constants/permissions.py).
export function canViewSystemJobs(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}
export function canManageSystemJobs(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}
export function canViewProductionReadiness(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN].includes(role);
}

export function canViewLaborCompliance(role) {
  return CAN_VIEW_LABOR_COMPLIANCE.includes(role);
}

export function canManageArticle36(role) {
  return CAN_MANAGE_ARTICLE36.includes(role);
}

export function canReviewLaborCompliance(role) {
  return CAN_REVIEW_LABOR_COMPLIANCE.includes(role);
}

// Mirrors backend _NOTICE_FULL grantees (constants/permissions.py).
export function canManageNotices(role) {
  return [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER].includes(role);
}

// Mirrors backend _MIGRATION_FULL grantees (constants/permissions.py) - can review/approve/
// activate migrated employee profiles. READ_ONLY_AUDITOR/LABOR_COMPLIANCE_OFFICER only get
// _MIGRATION_READONLY (view only, see canViewMigration below).
const CAN_MANAGE_MIGRATION = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER,
];
export function canManageMigration(role) {
  return CAN_MANAGE_MIGRATION.includes(role);
}
export function canViewMigration(role) {
  return CAN_MANAGE_MIGRATION.includes(role) || [ROLES.READ_ONLY_AUDITOR, ROLES.LABOR_COMPLIANCE_OFFICER].includes(role);
}

// Mirrors backend _SCHEDULING_FULL / _SCHEDULING_EDITOR / _SCHEDULING_REVIEWER grantees.
const CAN_VIEW_SCHEDULING = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER,
  ROLES.SCHEDULE_EDITOR, ROLES.SCHEDULE_REVIEWER,
];
const CAN_MANAGE_SHIFT_TEMPLATES = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER];
const CAN_EDIT_SCHEDULE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER, ROLES.SCHEDULE_EDITOR];
const CAN_APPROVE_SCHEDULE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER, ROLES.SCHEDULE_REVIEWER];

export function canViewScheduling(role) {
  return CAN_VIEW_SCHEDULING.includes(role);
}
// Mirrors backend SHIFT_SWAP_APPROVE grantee (constants/permissions.py _SCHEDULING_REVIEWER,
// the same role set as canApproveSchedule above).
export function canApproveShiftSwaps(role) {
  return CAN_APPROVE_SCHEDULE.includes(role);
}
// Mirrors backend SCHEDULE_READ_SELF/SCHEDULE_ACKNOWLEDGE_SELF grantee (constants/permissions.py
// _SCHEDULING_EMPLOYEE_SELF, granted to ROLES.EMPLOYEE only) - the employee-portal "My Schedule"
// page, distinct from canViewScheduling above which is the management-side scheduling module.
export function canViewOwnSchedule(role) {
  return role === ROLES.EMPLOYEE;
}
export function canManageShiftTemplates(role) {
  return CAN_MANAGE_SHIFT_TEMPLATES.includes(role);
}
export function canEditSchedule(role) {
  return CAN_EDIT_SCHEDULE.includes(role);
}
export function canApproveSchedule(role) {
  return CAN_APPROVE_SCHEDULE.includes(role);
}
// AVAILABILITY_REVIEW is granted to the same roles as scheduling edit/approve.
export function canReviewAvailability(role) {
  return CAN_VIEW_SCHEDULING.includes(role);
}

// Mirrors backend _TIMEKEEPING_MANAGE / _TIMEKEEPING_STORE_REVIEW grantees. Only
// SYSTEM_OWNER may test the live Google Drive connection (TIMECARD_SOURCE_CONNECT).
const CAN_MANAGE_TIMEKEEPING = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER];
const CAN_LOCK_PAYROLL_TIMECARDS = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.PAYROLL_PREPARER];
export function canManageTimekeeping(role) {
  return CAN_MANAGE_TIMEKEEPING.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}
export function canReviewTimecards(role) {
  return CAN_MANAGE_TIMEKEEPING.includes(role);
}
export function canLockPayrollTimecards(role) {
  return CAN_LOCK_PAYROLL_TIMECARDS.includes(role);
}
export function canTestDriveConnection(role) {
  return role === ROLES.SYSTEM_OWNER;
}

// Mirrors backend payroll permission sets (constants/permissions.py _PAYROLL_PREPARER_SET/
// _PAYROLL_REVIEWER_SET/_PAYROLL_PUBLISHER_SET). SYSTEM_OWNER/SYSTEM_ADMIN hold all three -
// segregation of duties between them is still enforced server-side per action.
const CAN_PREPARE_PAYROLL = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.PAYROLL_PREPARER];
const CAN_REVIEW_PAYROLL = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.PAYROLL_REVIEWER];
const CAN_PUBLISH_PAYROLL = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.PAYROLL_PUBLISHER];
export function canPreparePayroll(role) {
  return CAN_PREPARE_PAYROLL.includes(role);
}
export function canReviewPayroll(role) {
  return CAN_REVIEW_PAYROLL.includes(role);
}
export function canPublishPayroll(role) {
  return CAN_PUBLISH_PAYROLL.includes(role);
}
export function canViewPayroll(role) {
  return CAN_PREPARE_PAYROLL.includes(role) || CAN_REVIEW_PAYROLL.includes(role) || CAN_PUBLISH_PAYROLL.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}

// Mirrors backend MEDICAL_REQUIREMENT_ASSIGN/MEDICAL_STATUS_UPDATE grantees.
const CAN_MANAGE_MEDICAL_CHECKS = [
  ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.ONBOARDING_MANAGER,
  ROLES.MEDICAL_COMPLIANCE_REVIEWER,
];
export function canManageMedicalChecks(role) {
  return CAN_MANAGE_MEDICAL_CHECKS.includes(role);
}

// --- Training / LMS (Phase 6) - mirrors backend _TRAINING_FULL/_TRAINING_MANAGE/
// _TRAINING_LEGAL_REVIEW/_TRAINING_OPERATIONAL_REVIEW/_TRAINING_READONLY grantees.
const CAN_MANAGE_TRAINING_CONTENT = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.TRAINING_MANAGER];
const CAN_PUBLISH_TRAINING = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.TRAINING_MANAGER];
const CAN_APPROVE_TRAINING_CONTENT = [...CAN_MANAGE_TRAINING_CONTENT, ROLES.LABOR_COMPLIANCE_OFFICER];
const CAN_REVIEW_TRAINING_SOURCE = [...CAN_MANAGE_TRAINING_CONTENT, ROLES.LABOR_COMPLIANCE_OFFICER, ROLES.STORE_MANAGER];
const CAN_ASSIGN_TRAINING = [...CAN_MANAGE_TRAINING_CONTENT, ROLES.STORE_MANAGER];
const CAN_WAIVE_TRAINING = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.TRAINING_MANAGER];
const CAN_VIEW_TRAINING = [...CAN_REVIEW_TRAINING_SOURCE, ROLES.READ_ONLY_AUDITOR];
export function canManageTrainingContent(role) {
  return CAN_MANAGE_TRAINING_CONTENT.includes(role);
}
export function canPublishTraining(role) {
  return CAN_PUBLISH_TRAINING.includes(role);
}
export function canApproveTrainingContent(role) {
  return CAN_APPROVE_TRAINING_CONTENT.includes(role);
}
export function canReviewTrainingSource(role) {
  return CAN_REVIEW_TRAINING_SOURCE.includes(role);
}
export function canAssignTraining(role) {
  return CAN_ASSIGN_TRAINING.includes(role);
}
export function canWaiveTraining(role) {
  return CAN_WAIVE_TRAINING.includes(role);
}
export function canViewTraining(role) {
  return CAN_VIEW_TRAINING.includes(role);
}

// --- Paid leave ledger (new) - mirrors backend LEAVE_LEDGER_MANAGE grantees.
const CAN_MANAGE_LEAVE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canManageLeave(role) {
  return CAN_MANAGE_LEAVE.includes(role);
}
export function canViewLeave(role) {
  return CAN_MANAGE_LEAVE.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}

// --- Holiday & work calendar - mirrors backend _WORK_CALENDAR_FULL grantees.
const CAN_MANAGE_WORK_CALENDAR = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canManageWorkCalendar(role) {
  return CAN_MANAGE_WORK_CALENDAR.includes(role);
}
export function canApproveWorkCalendar(role) {
  return CAN_MANAGE_WORK_CALENDAR.includes(role);
}
export function canViewWorkCalendar(role) {
  return CAN_MANAGE_WORK_CALENDAR.includes(role) || [ROLES.STORE_MANAGER, ROLES.READ_ONLY_AUDITOR, ROLES.PAYROLL_PREPARER, ROLES.PAYROLL_REVIEWER, ROLES.PAYROLL_PUBLISHER].includes(role);
}

// --- Manual payment confirmation & evidence - mirrors backend _PAYMENT_EVIDENCE_FULL.
const CAN_MANAGE_PAYMENT_EVIDENCE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canRecordPaymentEvidence(role) {
  return CAN_MANAGE_PAYMENT_EVIDENCE.includes(role) || role === ROLES.PAYROLL_PUBLISHER;
}
export function canConfirmPaymentEvidence(role) {
  return CAN_MANAGE_PAYMENT_EVIDENCE.includes(role) || role === ROLES.PAYROLL_REVIEWER;
}
export function canViewPaymentEvidence(role) {
  return CAN_MANAGE_PAYMENT_EVIDENCE.includes(role) || [ROLES.PAYROLL_PREPARER, ROLES.PAYROLL_REVIEWER, ROLES.PAYROLL_PUBLISHER, ROLES.READ_ONLY_AUDITOR].includes(role);
}

// --- Payroll administration package - mirrors backend _PAYROLL_ADMIN_PACKAGE_FULL.
const CAN_MANAGE_PAYROLL_PACKAGE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.PAYROLL_PUBLISHER];
export function canManagePayrollPackage(role) {
  return CAN_MANAGE_PAYROLL_PACKAGE.includes(role);
}

// --- Google Drive timecard monthly ingestion readiness.
const CAN_MANAGE_DRIVE_INGESTION = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canManageDriveIngestion(role) {
  return CAN_MANAGE_DRIVE_INGESTION.includes(role);
}

// --- Email delivery queue.
const CAN_MANAGE_EMAIL_DELIVERY = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canManageEmailDelivery(role) {
  return CAN_MANAGE_EMAIL_DELIVERY.includes(role);
}

// --- Manager digest & data quality.
const CAN_VIEW_MANAGER_DIGEST = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER];
export function canViewManagerDigest(role) {
  return CAN_VIEW_MANAGER_DIGEST.includes(role);
}
const CAN_MANAGE_DATA_QUALITY = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
export function canViewDataQuality(role) {
  return CAN_MANAGE_DATA_QUALITY.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}
export function canManageDataQuality(role) {
  return CAN_MANAGE_DATA_QUALITY.includes(role);
}

// --- Health Committee (安全衛生委員会) - mirrors backend _HEALTH_COMMITTEE_FULL grantees.
const CAN_MANAGE_HEALTH_COMMITTEE = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.LABOR_COMPLIANCE_OFFICER];
export function canManageHealthCommittee(role) {
  return CAN_MANAGE_HEALTH_COMMITTEE.includes(role);
}
export function canViewHealthCommittee(role) {
  return CAN_MANAGE_HEALTH_COMMITTEE.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}

// --- Long-Hour Worker Medical Interview System - mirrors backend _LONG_HOUR_INTERVIEW_FULL/
// _LONG_HOUR_INTERVIEW_READONLY/LONG_HOUR_INTERVIEW_PHYSICIAN_DETAIL_READ grantees.
const CAN_MANAGE_LONG_HOUR_INTERVIEW = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER];
const CAN_VIEW_PHYSICIAN_DETAIL = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.MEDICAL_COMPLIANCE_REVIEWER];
export function canManageLongHourInterview(role) {
  return CAN_MANAGE_LONG_HOUR_INTERVIEW.includes(role);
}
export function canViewLongHourInterview(role) {
  return CAN_MANAGE_LONG_HOUR_INTERVIEW.includes(role) || [ROLES.LABOR_COMPLIANCE_OFFICER, ROLES.READ_ONLY_AUDITOR, ROLES.MEDICAL_COMPLIANCE_REVIEWER].includes(role);
}
export function canViewPhysicianDetail(role) {
  return CAN_VIEW_PHYSICIAN_DETAIL.includes(role);
}

// --- Corrective Action case tracker - mirrors backend _CORRECTIVE_ACTION_FULL grantees.
const CAN_MANAGE_CORRECTIVE_ACTION = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.LABOR_COMPLIANCE_OFFICER];
export function canManageCorrectiveAction(role) {
  return CAN_MANAGE_CORRECTIVE_ACTION.includes(role);
}
export function canViewCorrectiveAction(role) {
  return CAN_MANAGE_CORRECTIVE_ACTION.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}

// --- Supply pickup assignments - mirrors backend _SUPPLY_PICKUP_FULL grantees.
const CAN_MANAGE_SUPPLY_PICKUP = [ROLES.SYSTEM_OWNER, ROLES.SYSTEM_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.STORE_MANAGER];
export function canManageSupplyPickup(role) {
  return CAN_MANAGE_SUPPLY_PICKUP.includes(role);
}
export function canViewSupplyPickup(role) {
  return CAN_MANAGE_SUPPLY_PICKUP.includes(role) || role === ROLES.READ_ONLY_AUDITOR;
}
